import React, { useEffect, useState, useCallback } from "react";
import {
  Users, UserCheck, UserX, Activity, Coffee, TriangleAlert, Presentation,
  Percent, Gauge, Timer, RefreshCw, Clock3, CircleUser,
} from "lucide-react";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { KpiCard } from "@/components/KpiCard";
import { PageHeader, Card, SectionTitle, Empty } from "@/components/ui/page";
import { fmtTime } from "@/lib/format";

const PIE_COLORS = { working: "#10B981", on_break: "#F59E0B", excess: "#EF4444", meeting: "#3B82F6", away: "#6B7280" };

export default function Dashboard({ role }) {
  const [d, setD] = useState(null);
  const [auto, setAuto] = useState(true);
  const [updated, setUpdated] = useState(null);
  const endpoint = role === "admin" ? "/admin/dashboard" : "/manager/dashboard";

  const load = useCallback(() => {
    api.get(endpoint).then((r) => { setD(r.data); setUpdated(new Date()); }).catch(() => {});
  }, [endpoint]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!auto) return;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [auto, load]);

  if (!d) return <div className="py-20 text-center text-slate-400">Loading dashboard…</div>;

  const cards = [
    { label: "Total Headcount", value: d.total_employees, icon: Users, accent: "#0B1F33", tid: "kpi-total" },
    { label: "Present", value: d.present, icon: UserCheck, accent: "#10B981", tid: "kpi-present" },
    { label: "Absent", value: d.absent, icon: UserX, accent: "#EF4444", tid: "kpi-absent" },
    { label: "Working", value: d.working, icon: Activity, accent: "#10B981", tid: "kpi-working" },
    { label: "On Break", value: d.on_break, icon: Coffee, accent: "#F59E0B", tid: "kpi-break" },
    { label: "In Meeting", value: d.meeting, icon: Presentation, accent: "#3B82F6", tid: "kpi-meeting" },
    { label: "Away / Idle", value: d.away, icon: CircleUser, accent: "#6B7280", tid: "kpi-away" },
    { label: "Excess Break", value: d.excess_break, icon: TriangleAlert, accent: "#EF4444", tid: "kpi-excess" },
    { label: "Break Exceptions", value: d.exceptions, icon: TriangleAlert, accent: "#F97316", tid: "kpi-exceptions" },
    { label: "Attendance %", value: `${d.attendance_pct}%`, icon: Percent, accent: "#08A7A0", tid: "kpi-attendance" },
    { label: "Break Utilization", value: `${d.break_utilization}%`, icon: Gauge, accent: "#8B5CF6", tid: "kpi-utilization" },
    { label: "Avg Break", value: `${d.avg_break_min}m`, icon: Timer, accent: "#0EA5E9", tid: "kpi-avgbreak" },
  ];
  if (role === "admin") {
    cards.splice(1, 0, { label: "Active Employees", value: d.active_employees, icon: UserCheck, accent: "#08A7A0", tid: "kpi-active" });
    cards.splice(2, 0, { label: "Inactive", value: d.inactive_employees, icon: UserX, accent: "#94A3B8", tid: "kpi-inactive" });
  }

  const pieData = [
    { name: "Working", key: "working", value: d.working },
    { name: "On Break", key: "on_break", value: d.on_break },
    { name: "Meeting", key: "meeting", value: d.meeting },
    { name: "Away", key: "away", value: d.away },
  ].filter((x) => x.value > 0);

  return (
    <div>
      <PageHeader
        title={role === "admin" ? "Admin Dashboard" : "Manager Dashboard"}
        subtitle={role === "admin" ? "Organization-wide workforce overview" : "Your assigned teams & processes"}
        testId="dashboard-header"
      >
        <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-500">
          <Clock3 className="h-3.5 w-3.5" /> Last updated {updated ? updated.toLocaleTimeString("en-IN", { hour12: false }) : "—"}
        </div>
        <button
          data-testid="auto-refresh-toggle"
          onClick={() => setAuto((a) => !a)}
          className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold transition ${auto ? "bg-teal-50 text-teal-700" : "bg-slate-100 text-slate-500"}`}
        >
          <span className={`h-2 w-2 rounded-full ${auto ? "bg-[#08A7A0] gs-live-dot" : "bg-slate-400"}`} /> Auto {auto ? "ON" : "OFF"}
        </button>
        <button data-testid="refresh-btn" onClick={load} className="flex items-center gap-2 rounded-lg bg-[#0B1F33] px-3 py-2 text-xs font-semibold text-white hover:bg-[#12304d]">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </PageHeader>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
        {cards.map((c, i) => (
          <KpiCard key={c.tid} label={c.label} value={c.value} icon={c.icon} accent={c.accent} testId={c.tid} delay={i * 30} />
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="p-5 lg:col-span-2" testId="process-table-card">
          <SectionTitle>Process-wise Headcount</SectionTitle>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <th className="pb-2.5">Process</th>
                  <th className="pb-2.5 text-center">HC</th>
                  <th className="pb-2.5 text-center">Working</th>
                  <th className="pb-2.5 text-center">Break</th>
                  <th className="pb-2.5 text-center">Away</th>
                  <th className="pb-2.5 text-center">Exceptions</th>
                  <th className="pb-2.5 text-center">Att %</th>
                  <th className="pb-2.5 text-center">Avg Break</th>
                </tr>
              </thead>
              <tbody>
                {d.process_rows.length === 0 && <tr><td colSpan={8}><Empty /></td></tr>}
                {d.process_rows.map((p) => (
                  <tr key={p.process} data-testid={`proc-row-${p.process}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                    <td className="py-3 font-semibold text-[#0B1F33]">{p.process}</td>
                    <td className="text-center">{p.hc}</td>
                    <td className="text-center text-emerald-600 font-semibold">{p.working}</td>
                    <td className="text-center text-amber-600 font-semibold">{p.break}</td>
                    <td className="text-center text-slate-500">{p.away}</td>
                    <td className="text-center"><span className={p.exceptions > 0 ? "font-bold text-red-600" : "text-slate-400"}>{p.exceptions}</span></td>
                    <td className="text-center">{p.attendance_pct}%</td>
                    <td className="text-center font-mono text-xs">{p.avg_break_min}m</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="p-5" testId="status-chart-card">
          <SectionTitle>Live Status Split</SectionTitle>
          {pieData.length ? (
            <div className="mt-2 h-56">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
                    {pieData.map((e) => <Cell key={e.key} fill={PIE_COLORS[e.key]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : <Empty label="No live activity" />}
          <div className="mt-2 flex flex-wrap justify-center gap-3">
            {pieData.map((e) => (
              <div key={e.key} className="flex items-center gap-1.5 text-xs text-slate-600">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: PIE_COLORS[e.key] }} /> {e.name} ({e.value})
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="mt-6 p-5" testId="process-bar-card">
        <SectionTitle>Workforce Distribution by Process</SectionTitle>
        <div className="mt-4 h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={d.process_rows}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
              <XAxis dataKey="process" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="working" stackId="a" fill="#10B981" name="Working" radius={[0, 0, 0, 0]} />
              <Bar dataKey="break" stackId="a" fill="#F59E0B" name="Break" />
              <Bar dataKey="away" stackId="a" fill="#94A3B8" name="Away" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
}
